/**
 * Run-phase helpers (#1580).
 *
 * A run only runs the phases the operator selected (Plan / Generate / Run /
 * Heal). A run that didn't run the execute phase produces no execution stats,
 * so the run summary shows what it DID produce (Planned / Generated) instead
 * of all-zero execution tiles. This predicate is the single source of that
 * decision, shared by the live run view and the run results page.
 */
import type { PipelinePhase, TestRun, UnifiedRunTemplate } from '@/types'

/** The four stages, in the order a run does them. */
const PHASE_ORDER: readonly PipelinePhase[] = ['plan', 'generate', 'execute', 'heal']

/** What each stage is called on screen (#2225).
 *
 * `execute` is the only one that differs, and it differs because "execute"
 * is not a word we put in front of an operator anywhere else. This mapping
 * was inline in `RunDetail`; three callers now need it, so it lives here
 * with the rest of the phase logic rather than being copied twice more.
 */
export function phaseLabel(phase: PipelinePhase): string {
  return phase === 'execute' ? 'Run'
    : phase === 'plan' ? 'Plan'
      : phase === 'generate' ? 'Generate' : 'Heal'
}

/**
 * The stages a template will run, in order (#2225).
 *
 * Answers "what is this button about to do", which the old Run settings page
 * answered by listing each template's phases and which the start bar dropped.
 * Read per kind, because each kind stores it differently:
 *
 *   - replay runs execute only, plus heal when the template heals failures;
 *   - generation carries an explicit `phases` list;
 *   - resume carries `phaseFilter`, and an empty one means all three.
 *
 * The defaults mirror `buildRunRequest`, which is what actually decides. A
 * template whose kind is unknown returns nothing rather than guessing — a
 * wrong answer here is worse than none, because it is a promise about what
 * a button will do.
 */
export function phasesForTemplate(
  template: Pick<UnifiedRunTemplate, 'kind' | 'phases' | 'phaseFilter' | 'healOnFailure'> | null | undefined,
): PipelinePhase[] {
  if (!template) return []
  if (template.kind === 'replay') {
    return template.healOnFailure ? ['execute', 'heal'] : ['execute']
  }
  if (template.kind === 'generation') {
    const phases = template.phases?.length ? template.phases : PHASE_ORDER
    return PHASE_ORDER.filter(p => phases.includes(p))
  }
  if (template.kind === 'resume') {
    const phases = template.phaseFilter?.length
      ? template.phaseFilter
      : (['generate', 'execute', 'heal'] as PipelinePhase[])
    return PHASE_ORDER.filter(p => phases.includes(p))
  }
  return []
}

/** Those stages as one operator-facing string, e.g. `Run → Heal`. Empty
 * when there is nothing to say, so a caller can render nothing at all. */
export function describePhases(
  template: Parameters<typeof phasesForTemplate>[0],
): string {
  return phasesForTemplate(template).map(phaseLabel).join(' → ')
}

/**
 * Whether a run actually ran its execute phase. `ranPhases` absent (runs
 * created before #1580) → treat as executed, so legacy runs keep the
 * execution view rather than flipping to an empty produced view.
 */
export function runDidExecute(ranPhases: TestRun['ranPhases'] | undefined): boolean {
  return ranPhases ? ranPhases.includes('execute') : true
}

/**
 * How many tests a run created — generated scripts if any, otherwise planned
 * cases. Uses `||` (not `??`) on purpose: a generation-failed or plan-only run
 * has `generatedCount: 0` with a non-zero `plannedCount`, and should still read
 * as "N created", not "0 created" (#1580).
 */
export function createdTestCount(run: { plannedCount?: number; generatedCount?: number }): number {
  return run.generatedCount || run.plannedCount || 0
}
